/**
 * Vehicle Detail modal — opens on a row click in the Vehicle Inventory table.
 * A lean, curated view: Website (Dealer.com), Certified (HomeNet's Dealer Cert next
 * to the listing's sticker and dealer notes — facts only; mismatches are worked on
 * the Inventory Audit), INVI (placeholder until per-car photo counts land), HomeNet,
 * Vehicle Details (Tekion core + both recon steps), vAuto, and the recon / vAuto notes. Fuller detail + history will live on the full-detail
 * page; the sections here will become shared building blocks it reuses.
 */
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { X } from "lucide-react"

import { THEME } from "../../config/theme"
import { DealerNotes, getDealerNotes, InventoryRecord, ReconMatch } from "../../api/inventory"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { formatUploadedAt } from "../../utils/formatAge"

interface Props {
  record: InventoryRecord | null
  onClose: () => void
}

const money = (value: number | null): string =>
  value == null ? "—" : `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
const num = (value: number | null): string => (value == null ? "—" : value.toLocaleString())
const text = (value: string | null): string => (value && value.trim() ? value : "—")
const yesNo = (value: boolean | null): string => (value == null ? "—" : value ? "Yes" : "No")

// How many of the notes' certified phrases show before "+ N more".
const PHRASES_SHOWN = 3

// Collapse identical recon notes across sources into one entry, labeled by the
// source(s) it came from — RapidRecon usually repeats the same note in the store
// file and the DOLANRECON catch-all, so showing it once keeps the modal readable.
function dedupeReconNotes(matches: ReconMatch[]): { label: string; text: string }[] {
  const sourcesByNote = new Map<string, string[]>()
  for (const match of matches) {
    if (!match.notes) continue
    const sources = sourcesByNote.get(match.notes) ?? []
    if (match.source) sources.push(match.source)
    sourcesByNote.set(match.notes, sources)
  }
  const differ = sourcesByNote.size > 1
  return Array.from(sourcesByNote, ([noteText, sources]) => ({
    // Combined when the store and recon center agree, headed by source when they differ.
    label: differ ? sources.join(", ") : "Recon notes",
    text: noteText,
  }))
}

export default function VehicleDetailModal({ record, onClose }: Props) {
  if (!record) return null
  const title =
    [record.year, record.make, record.model, record.trim].filter(Boolean).join(" ") || "Vehicle"
  const reconMatches = record.recon_matches ?? []
  const reconNotes = dedupeReconNotes(reconMatches)
  // "Recon step" = the selling store's step (a non-Recon-Center source); "Dolan Recon
  // step" = the central DOLANRECON catch-all's. Deriving both straight from recon_matches
  // keeps a DOLANRECON-only car from showing the catch-all step as the store's.
  const mainReconStep = reconMatches.find((m) => m.source !== "Recon Center")?.step ?? null
  const dolanReconStep = reconMatches.find((m) => m.source === "Recon Center")?.step ?? null

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(event) => event.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>{title}</div>
            <div style={subtitleStyle}>
              <span style={{ fontFamily: THEME.fonts.mono }}>{text(record.vin)}</span> · Stock{" "}
              {text(record.stock_number)}
            </div>
            <Link
              to={`/inventory/vehicles/${encodeURIComponent(record.vin ?? "")}/${encodeURIComponent(record.stock_number ?? "")}`}
              style={fullDetailsLinkStyle}
            >
              View full details →
            </Link>
          </div>
          <button style={closeStyle} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div style={bodyStyle}>
          <Section title="Website · Dealer.com">
            {/* Live on site doubles as the listing link: "View on site" when it's up, else "No". */}
            {record.website_live && record.website_url ? (
              <LinkField label="Live on site" href={record.website_url} text="View on site →" />
            ) : (
              <Field
                label="Live on site"
                value={record.website_live == null ? "—" : record.website_live ? "Yes" : "No"}
              />
            )}
            <Field label="Site photos" value={num(record.website_photos_count)} />
            <Field label="Website price" value={money(record.website_price)} />
            {record.website_carfax_url ? (
              <LinkField label="CARFAX" href={record.website_carfax_url} text="CARFAX →" />
            ) : (
              <Field label="CARFAX" value="—" />
            )}
          </Section>

          <CertifiedSection record={record} />

          <Section title="HomeNet">
            <Field label="State" value={text(record.website_status)} />
            {record.photo_matches && record.photo_matches.length > 0 ? (
              // Dual-listed (franchise + Lee Brothers wholesale) — show each count.
              record.photo_matches.map((match, index) => (
                <Field key={index} label={`${text(match.source)} photos`} value={num(match.photos)} />
              ))
            ) : (
              <Field label="HomeNet photos" value={num(record.photos_count)} />
            )}
            {/* INVI's own photo count — placeholder until wired from the photo-capture
                system (photo_capture_sessions/_photos by rooftop+VIN). Sits inline with HomeNet. */}
            <Field label="INVI photos" value="—" />
          </Section>

          <Section title="Vehicle Details">
            {/* Row 1 */}
            <Field label="Age (days)" value={num(record.age_days)} />
            <Field label="Store" value={text(record.rooftop)} />
            <Field label="Tekion status" value={text(record.tekion_status)} />
            <Field label="Title status" value={text(record.title_status)} />
            {/* Row 2 */}
            <Field label="Type" value={text(record.vehicle_type)} />
            <Field label="Recon step" value={text(mainReconStep)} />
            <Field label="Dolan Recon step" value={text(dolanReconStep)} />
            <Field label="Invoice" value={money(record.price_invoice)} />
          </Section>

          <Section title="vAuto">
            <Field label="Appraiser" value={text(record.appraiser)} />
            <Field label="KBB Lending" value={money(record.valuation_kbb)} />
            <Field label="KBB Trade-In" value={money(record.kbb_trade_in)} />
          </Section>

          <Section title="Recon Notes">
            {reconNotes.length > 0 ? (
              reconNotes.map((note, index) => (
                <div key={index} style={reconNoteBlockStyle}>
                  <div style={reconNoteSourceStyle}>{note.label}</div>
                  <div style={reconNoteTextStyle}>{note.text}</div>
                </div>
              ))
            ) : (
              <Field label="Recon notes" value="—" full />
            )}
          </Section>

          <Section title="vAuto Notes">
            <Field label="Notes" value={text(record.vauto_notes)} full />
          </Section>
        </div>
      </div>
    </div>
  )
}

// What the listing's certified sticker says. "—" when the car isn't live on the site:
// no listing, nothing to show a sticker on. A sticker without a program name is "Yes".
function stickerText(record: InventoryRecord): string {
  if (!record.website_live || record.website_cert_sticker == null) return "—"
  if (!record.website_cert_sticker) return "None"
  return record.website_cert_program?.trim() || "Yes"
}

// Always asks the backend — it decides which cars get read (used cars, by the site's
// own condition), so a stored reading is shown whatever Tekion calls the car. The
// "new car" wording only explains an absence.
function notesSummary(notes: DealerNotes | null, loadFailed: boolean, isNewCar: boolean): string {
  if (loadFailed) return "Couldn't load"
  if (!notes) return "Loading…"
  if (!notes.read_at) return isNewCar ? "Not read for new cars" : "Not read yet"
  const count = notes.certified_phrases.length
  if (count === 0) return "Don't mention Certified"
  return `Mention Certified · ${count} ${count === 1 ? "phrase" : "phrases"}`
}

// HomeNet's Dealer Cert beside what the site shows: the sticker (from the listing) and
// what the dealer notes say about certification, verbatim. Facts only — which notes
// wording counts as "the site says certified" is not decided yet, so nothing is flagged.
function CertifiedSection({ record }: { record: InventoryRecord }) {
  const { dealerGroupId } = useDealerGroup()
  const [notes, setNotes] = useState<DealerNotes | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [showAllPhrases, setShowAllPhrases] = useState(false)
  const [showFullNotes, setShowFullNotes] = useState(false)
  // Notes are read for used cars only (a new car can't be certified pre-owned), so
  // for a new car with no reading the summary says why rather than "not yet".
  const isNewCar = (record.vehicle_type ?? "").trim().toLowerCase() === "new"

  // Load this car's dealer notes when the modal opens on it.
  useEffect(() => {
    setNotes(null)
    setLoadFailed(false)
    setShowAllPhrases(false)
    setShowFullNotes(false)
    if (!record.vin) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await getDealerNotes(record.vin ?? "", dealerGroupId)
        if (!cancelled) setNotes(response)
      } catch {
        if (!cancelled) setLoadFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [record.vin, dealerGroupId, isNewCar])

  const phrases = notes?.certified_phrases ?? []
  const shownPhrases = showAllPhrases ? phrases : phrases.slice(0, PHRASES_SHOWN)
  const hiddenCount = phrases.length - shownPhrases.length
  const hasNotesText = Boolean(notes?.notes && notes.notes.trim())

  return (
    <Section title="Certified">
      <Field label="HomeNet Dealer Cert" value={yesNo(record.hn_dealer_cert)} />
      <Field label="Sticker on site" value={stickerText(record)} />
      <Field label="Dealer notes" value={notesSummary(notes, loadFailed, isNewCar)} />
      <Field label="Notes read" value={formatUploadedAt(notes?.read_at) || "—"} />
      {(phrases.length > 0 || hasNotesText) && (
        <div style={{ gridColumn: "1 / -1" }}>
          {phrases.length > 0 && (
            <>
              <div style={fieldLabelStyle}>What the notes say</div>
              {shownPhrases.map((phrase, index) => (
                <div key={index} style={phraseStyle}>
                  “{phrase}”
                </div>
              ))}
              {hiddenCount > 0 && (
                <button style={textButtonStyle} onClick={() => setShowAllPhrases(true)}>
                  + {hiddenCount} more
                </button>
              )}
            </>
          )}
          {hasNotesText && (
            <div>
              <button style={textButtonStyle} onClick={() => setShowFullNotes((open) => !open)}>
                {showFullNotes ? "Hide full dealer notes ▾" : "Show full dealer notes ▸"}
              </button>
              {showFullNotes && <div style={reconNoteTextStyle}>{notes?.notes}</div>}
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>{title}</div>
      <div style={gridStyle}>{children}</div>
    </div>
  )
}

function Field({ label, value, full = false }: { label: string; value: string; full?: boolean }) {
  return (
    <div style={full ? { gridColumn: "1 / -1" } : undefined}>
      <div style={fieldLabelStyle}>{label}</div>
      <div style={{ ...fieldValueStyle, whiteSpace: full ? "pre-wrap" : "normal" }}>{value}</div>
    </div>
  )
}

function LinkField({ label, href, text }: { label: string; href: string; text: string }) {
  return (
    <div>
      <div style={fieldLabelStyle}>{label}</div>
      <a href={href} target="_blank" rel="noopener noreferrer" style={linkValueStyle}>
        {text}
      </a>
    </div>
  )
}

// --- Styles ---

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const panelStyle: React.CSSProperties = {
  background: THEME.colors.white,
  borderRadius: THEME.borderRadius.md,
  width: 880,
  maxWidth: "94vw",
  maxHeight: "88vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
  fontFamily: THEME.fonts.primary,
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: `${THEME.spacing.md} ${THEME.spacing.lg}`,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
}

const titleStyle: React.CSSProperties = {
  fontSize: THEME.typography.h4.size,
  fontWeight: THEME.typography.h4.weight,
  color: THEME.colors.text,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
}

const fullDetailsLinkStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: THEME.spacing.xs,
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.navy,
  textDecoration: "none",
}

const closeStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  display: "flex",
  padding: 0,
}

const bodyStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  overflowY: "auto",
}

const sectionStyle: React.CSSProperties = {
  marginBottom: THEME.spacing.lg,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.navy,
  marginBottom: THEME.spacing.sm,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  paddingBottom: THEME.spacing.xs,
}

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: THEME.spacing.md,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
}

const fieldValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.text,
  marginTop: 2,
  wordBreak: "break-word",
}

const linkValueStyle: React.CSSProperties = {
  ...fieldValueStyle,
  display: "inline-block",
  color: THEME.colors.navy,
  textDecoration: "underline",
  cursor: "pointer",
}

// Recon-notes blocks — one per source when the store and recon center disagree. The
// accent bar + bold source header make the split between the two obvious.
const reconNoteBlockStyle: React.CSSProperties = {
  gridColumn: "1 / -1",
  borderLeft: `3px solid ${THEME.colors.navy}`,
  paddingLeft: THEME.spacing.sm,
  marginTop: THEME.spacing.sm,
}

const reconNoteSourceStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  color: THEME.colors.navy,
  marginBottom: 2,
}

const reconNoteTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.text,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}

const phraseStyle: React.CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: THEME.colors.text,
  background: THEME.colors.neutral.light.background,
  borderRadius: THEME.borderRadius.sm,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  marginTop: THEME.spacing.xs,
  wordBreak: "break-word",
}

const textButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  marginTop: THEME.spacing.sm,
  marginRight: THEME.spacing.md,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.navy,
  fontFamily: THEME.fonts.primary,
}
